import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getTranslation, getTranslations, LanguageCode, TranslationKey, ISO_CODE_EN, ISO_CODE_FR } from "../utils/translations";

/**
 * Hook to get translations based on language from URL params or pathname
 * 
 * @example
 * const t = useTranslation();
 * <h1>{t('indexDescription')}</h1>
 * 
 * @example
 * const t = useTranslation();
 * const allTranslations = t.translations;
 */
export const useTranslation = () => {
  const params = useParams();
  const location = useLocation();
  
  // Try to get language from route params first, then from pathname
  let language: LanguageCode = ISO_CODE_EN;
  
  if (params.lang) {
    language = params.lang.toLowerCase() as LanguageCode;
  } else {
    // Check pathname for language pattern (e.g., /index/BUBIOG/fr)
    const pathMatch = location.pathname.match(/\/([a-z]{2})$/);
    if (pathMatch && (pathMatch[1] === ISO_CODE_FR || pathMatch[1] === ISO_CODE_EN)) {
      language = pathMatch[1] as LanguageCode;
    }
  }

  const t = useMemo(
    () => (key: TranslationKey) => getTranslation(key, language),
    [language]
  );

  const translations = useMemo(
    () => getTranslations(language),
    [language]
  );

  return {
    t,
    translations,
    language: language as LanguageCode,
  };
};
